import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { buildExpenditureLedger, ExpenditureLedgerSummary } from '../../api/expenditureLedgerApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the financials PDF. Its "Service Charge Expenditure by Schedule - Summary" report is found by name, even when it runs over several pages.',
  'Upload the ledger export (.xlsx, .xls or .csv) that contains the EXPENDITURE LEDGERS table.',
  'Click Build Expenditure Ledger. Each particular gets its PDF TOTAL plus its Accruals, Prepayment and Reclass lines, split by Debit and Credit. Expenditure_Ledger.xlsx downloads automatically.',
];

const money = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function ExpenditureLedgerPanel({ mission, onBack }: PanelProps) {
  const [pdf, setPdf] = useState<File[]>([]);
  const [ledger, setLedger] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<ExpenditureLedgerSummary>>();

  const handleSubmit = async () => {
    if (pdf.length === 0 || ledger.length === 0) {
      fail('Upload both the financials PDF and the ledger file.');
      return;
    }
    await run(() => buildExpenditureLedger(pdf[0], ledger[0]), 'Building the expenditure ledger failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop
          label="Financials PDF"
          required
          accept=".pdf"
          hint="PDF with the expenditure summary report"
          accentColor={mission.color}
          files={pdf}
          onFiles={setPdf}
        />
        <FileDrop
          label="Ledger export"
          required
          accept=".xlsx,.xls,.csv"
          hint="XLSX, XLS or CSV with EXPENDITURE LEDGERS"
          accentColor={mission.color}
          files={ledger}
          onFiles={setLedger}
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Building…' : 'Build Expenditure Ledger'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Building…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['Particulars', s.particulars],
              ['PDF total', money(s.pdf_total)],
              ['Filtered ledger lines', Object.values(s.filtered_lines).reduce((a, b) => a + b, 0)],
              ['Ledger-only particulars', s.ledger_only.length],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                <div className="text-xl font-bold text-foreground">{value}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Object.entries(s.adjustment_totals).map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border px-3 py-2">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="font-semibold text-foreground">{money(value)}</div>
              </div>
            ))}
          </div>
          {s.fuzzy_matches.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              Matched by similar name:{' '}
              {s.fuzzy_matches.map((m) => `${m.ledger} → ${m.particular}`).join('; ')}. Check these rows.
            </div>
          )}
          {s.ledger_only.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              Not in the PDF summary, added from the ledger only: {s.ledger_only.join(', ')}.
            </div>
          )}
          {s.warnings.map((w) => (
            <div key={w} className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
              ⚠ {w}
            </div>
          ))}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload both files above to build the expenditure ledger.</InfoNotice>}
    </MissionShell>
  );
}

export default ExpenditureLedgerPanel;
